import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, CheckIcon, TruckIcon } from 'lucide-react';
import { requestOffer } from '../../utils/offerBus';
import { onBuilderStepRequest } from '../../utils/deepLinks';
import { BUILDER_ID, builderPrices, builderSteps, essentialIncludes } from '../../data/builder';
import { SHIPPING_LINE, WARRANTY_LINE } from '../../data/product';
import { useBuilder } from '../../hooks/useBuilder';
import { useElementInView } from '../../hooks/useElementInView';
import { useTrackOnView } from '../../hooks/useTrackOnView';
import { money } from '../QuantityStepper';
import { BuilderVisual } from './BuilderVisual';
import { BuilderStepPanel } from './BuilderStepPanel';
import { BuilderSummary, BuilderSummaryBar } from './BuilderSummary';
import { CompleteUpgradeNotice } from './CompleteUpgradeNotice';
import { HardCaseDownsell } from './HardCaseDownsell';
import { UspsBadge } from '../UspsBadge';
import { SecureCheckoutNote } from '../SecureCheckoutNote';
export function BuildYourX1() {
  const builder = useBuilder();
  const reduce = useReducedMotion();
  const viewRef = useTrackOnView<HTMLElement>('build_your_x1_view');
  const inView = useElementInView(BUILDER_ID, '-30% 0px -30% 0px');
  const {
    state,
    step
  } = builder;
  const index = builderSteps.findIndex(item => item.id === step);
  const [pulse, setPulse] = useState(0);

  // Any "Build your X1" link: let the smooth scroll land, then briefly highlight the builder.
  useEffect(() => {
    let timer = 0;
    const onClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement | null)?.closest('a');
      const href = link?.getAttribute('href');
      if (!link || href !== `#${BUILDER_ID}` && href !== '#offer') return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPulse(count => count + 1), 750);
    };
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
      window.clearTimeout(timer);
    };
  }, []);

  // Deep links and section buttons (#build, "Add RX to my build") open a specific step.
  const goToStepRef = useRef(builder.goToStep);
  goToStepRef.current = builder.goToStep;
  useEffect(() => {
    let timer = 0;
    const off = onBuilderStepRequest(next => {
      goToStepRef.current(next);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPulse(count => count + 1), 750);
    });
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);
  const stepDone = (id: string) => id === 'lens' && step !== 'lens' || id === 'lensSystem' && (state.fullLens || state.complete) || id === 'case' && (state.hardCase || state.complete) || id === 'care' && state.careKit || id === 'rx' && state.rx > 0 || id === 'protection' && state.protection !== 'basic';
  return <section ref={viewRef} id={BUILDER_ID} data-builder="true" aria-labelledby="builder-heading" className="relative scroll-mt-24 border-b border-white/10 bg-ink-950" data-id="element-270" data-mp="2s6t17953xbpf">
      {pulse > 0 && <motion.span key={pulse} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 border-2 border-grit-500" initial={{
      opacity: 0
    }} animate={{
      opacity: reduce ? 0 : [0, 1, 0]
    }} transition={{
      duration: 0.3,
      times: [0, 0.35, 1],
      ease: 'easeOut'
    }} data-id="element-271" data-mp="1bnhlewpp3ad2" />}
      <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16" data-id="element-272" data-mp="sy9w7wuhif5q">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between" data-id="element-273" data-mp="xxwam9m1u1g4">
          <div data-id="element-274" data-mp="xw34cvfclj8n">
            <p className="font-display text-xl font-extrabold uppercase tracking-wide text-grit-500" data-id="element-275" data-mp="12ocp7x1lvt42">
              Your lens. Your setup. Your way.
            </p>
            <h2 id="builder-heading" className="grit-headline mt-1 text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-276" data-mp="574ibn83twy2">
              Build your <span className="text-grit-500" data-id="element-277" data-mp="2bwppp1z2u0ep">X1.</span>
            </h2>
            <p className="mt-3 text-lg font-medium text-steel-300" data-id="element-278" data-mp="27vw2ybxderne">
              Choose your lens. Add only what you need. Watch it come together.
            </p>
            <a href="#pack-single" onClick={event => {
            event.preventDefault();
            requestOffer('single');
          }} className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 hover:text-grit-500 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-279" data-mp="2lolnsd15xqix">
              Want a ready-made kit? Complete Systems &amp; deals below
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-280" data-mp="2zkz3bgm4zhur" />
            </a>
          </div>
          <div className="lg:text-right" data-id="element-281" data-mp="1aa86jr0x09bv">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-282" data-mp="159u6ow9gw5ox">GritVue X1 Essential</p>
            <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-none text-offwhite" data-id="element-283" data-mp="3gxfkf1cuv8n2">
              Starting at <span className="text-grit-500" data-id="element-284" data-mp="3luz3ceki09lc">{money(builderPrices.essential)}</span>
            </p>
            <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-steel-300 lg:justify-end" data-id="element-285" data-mp="383u2j95e9b9f">
              <TruckIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-286" data-mp="2y5wiyyyhc2n5" />
              {SHIPPING_LINE}
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10" data-id="element-287" data-mp="336kfg05h9bor">
          {/* The product stays anchored in view while the build changes it */}
          <div className="sticky top-[5.25rem] z-20 -mx-4 self-start bg-ink-950 px-4 pb-3 sm:-mx-6 sm:px-6 lg:top-24 lg:mx-0 lg:px-0 lg:pb-0" data-id="element-288" data-mp="2t6a3eiv6iab6">
            <BuilderVisual state={state} animateLenses={builder.step === 'lensSystem'} data-id="element-289" data-mp="nemu2ll2hk2d" />
            <ul className="mt-4 hidden gap-x-6 gap-y-1.5 sm:grid sm:grid-cols-3" data-id="element-290" data-mp="rewyvux4i4fn">
              {essentialIncludes.map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-steel-400" data-id="element-291" data-mp="20a0w3zia3g1r">
                  <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-292" data-mp="1vfn6kdqas350" />
                  {item}
                </li>)}
            </ul>
          </div>

          <div id="builder-panel" className="grid scroll-mt-[26rem] content-start gap-4 lg:scroll-mt-28" data-id="element-293" data-mp="1meoxvi4wz7g3">
            {/* Mobile: one readable step label and a thin progress bar */}
            <div className="lg:hidden" aria-live="polite" data-id="element-294" data-mp="2f230nwvamx84">
              <p className="flex items-baseline justify-between text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-295" data-mp="2a9ro9ujd9iyo">
                <span data-id="element-296" data-mp="25aykwuxsn8c8">
                  Step {index + 1} of {builderSteps.length}
                </span>
                <span className="font-display text-lg tracking-wide text-offwhite" data-id="element-297" data-mp="32s6s75v7j6ir">{builderSteps[index]?.label}</span>
              </p>
              <div className="mt-2 h-1 w-full bg-white/10" data-id="element-298" data-mp="35ba9jhtj7gp7">
                <motion.div className="h-full origin-left bg-grit-500" animate={{
                scaleX: (index + 1) / builderSteps.length
              }} transition={{
                duration: reduce ? 0 : 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-299" data-mp="35d4s4phbywq4" />
              </div>
            </div>

            {/* Desktop: full step navigation */}
            <nav aria-label="Build steps" className="hidden lg:block" data-id="element-300" data-mp="3oao1xvbm4jdl">
              <ol className="grid grid-cols-7 border border-white/10" data-id="element-301" data-mp="3t996u81t7r8d">
                {builderSteps.map((item, i) => {
                const current = item.id === step;
                const done = stepDone(item.id);
                return <li key={item.id} className={i > 0 ? 'border-l border-white/10' : ''} data-id="element-302" data-mp="6sd2bawxavdr" data-mpi={i}>
                      <button type="button" onClick={() => builder.goToStep(item.id)} aria-current={current ? 'step' : undefined} className={`flex h-full w-full flex-col items-center gap-1 px-1 py-2.5 transition-colors duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-grit-500 ${current ? 'bg-grit-500 text-ink-950' : 'text-steel-300 hover:bg-white/[0.04] hover:text-offwhite'}`} data-id="element-303" data-mp="awb3dr5t6g9g">
                        <span className={`flex h-5 w-5 items-center justify-center text-[11px] font-extrabold ${current ? '' : done ? 'bg-grit-500 text-ink-950' : 'border border-white/25'}`} data-id="element-304" data-mp="ifbhdoppjwyt">
                          {done && !current ? <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-305" data-mp="nbtj0fpqrf6x" /> : i + 1}
                        </span>
                        <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.1em]" data-id="element-306" data-mp="3quv4s73p6l5y">{item.label}</span>
                      </button>
                    </li>;
              })}
              </ol>
            </nav>

            <CompleteUpgradeNotice hint={builder.hint} complete={state.complete} onUpgrade={builder.upgradeToComplete} onRevert={builder.revertToEssential} data-id="element-307" data-mp="2b4lwbvzq2ds6" />

            <BuilderStepPanel builder={builder} data-id="element-308" data-mp="21b55n0uvtesq" />

            {step !== 'review' && <>
                <div className="hidden lg:block" data-id="element-309" data-mp="2u255r0q3dyf4">
                  <BuilderSummary builder={builder} data-id="element-310" data-mp="2u0u2d20k6soj" />
                </div>
                {/* Phones: the summary lives in the bottom bar, so show shipping + payment trust here */}
                <div className="border border-white/10 bg-ink-900 p-4 lg:hidden" data-id="element-311" data-mp="2k2xwopqe4pxt">
                  <UspsBadge note="Free U.S. shipping." data-id="element-312" data-mp="14mpbergtuh9a" />
                  <SecureCheckoutNote className="mt-4" data-id="element-313" data-mp="14pbkwwna36u5" />
                </div>
              </>}
          </div>
        </div>

        {/* Keep people scrolling: the ready-made kits and pack deals are right below */}
        <div className="mt-12 flex flex-col gap-5 border-t-4 border-grit-500 bg-ink-900 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between" data-id="element-314" data-mp="snp6cyg6kgay">
          <div data-id="element-315" data-mp="ew27qryrmvp0">
            <p className="font-display text-3xl font-extrabold uppercase leading-none text-offwhite sm:text-4xl" data-id="element-316" data-mp="euhdl3gd5ero">
              Don&apos;t want to build it? <span className="text-grit-500" data-id="element-317" data-mp="4xusqeo1enpn">Keep scrolling.</span>
            </p>
            <p className="mt-3 max-w-2xl text-[15px] text-steel-300" data-id="element-318" data-mp="9xh3mhge7rva">
              Ready-made Complete Systems with all 3 lenses and the hard case, plus multi-pack deals. {WARRANTY_LINE}{' '}
              {SHIPPING_LINE}
            </p>
          </div>
          <a href="#pack-single" onClick={event => {
          event.preventDefault();
          requestOffer('single');
        }} className="inline-flex shrink-0 items-center justify-center gap-2 border-2 border-grit-500 px-6 py-4 font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color,transform] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-319" data-mp="3w57b2x0np775">
            See Complete Systems &amp; deals
            <ArrowDownIcon className="h-5 w-5" aria-hidden="true" data-id="element-320" data-mp="3jt4v0ad4fq3d" />
          </a>
        </div>
      </div>

      <BuilderSummaryBar builder={builder} visible={inView} data-id="element-321" data-mp="2g2hwaajlze0" />
      <HardCaseDownsell open={builder.downsellOpen} total={builder.total} onAccept={builder.acceptDownsell} onDecline={builder.declineDownsell} onClose={builder.closeDownsell} data-id="element-322" data-mp="3tj7rikjgbm6g" />
    </section>;
}